import React, { useRef, useState } from "react";

import type { AutosaveEntitySnapshot } from "shared/autosave";
import { Select } from "shared/components/Form/Select";
import Spacer from "shared/components/Spacer";
import { SmallText } from "shared/components/Typography";
import type {
  Vero1ChecklistItem,
  Vero1ItemEdit,
  Vero1ReasonCatalog,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import { Vero1RowKind } from "shared/interfaces/api/vero1";

import { AUTOSAVE_STRINGS, autosaveTimeLabel } from "./autosaveStrings";
import { rowFactValue } from "./ChecklistRow";
import {
  EditorContainer,
  EditorField,
  EditorLabel,
  EditorHint,
  LumenReference,
  LumenValue,
  LONG_NAME_CHARS,
  NameTray,
  NameTrayLabel,
  SeverityStack,
  SeverityStackName,
  SeverityStackWord,
  SelectField,
  SelectFieldMenuStyles,
  VERO1_DROPDOWN_CLASS,
  SeverityToggle,
  SeverityToggleGroup,
  RequiredMark,
} from "./editorStyled";
import {
  NO_VERDICT_LABEL,
  SEVERITY_WORDS,
  availableSeverities,
  checkStateLabel,
  clientNameFor,
  displayName,
  effectiveTone,
  formatItemValue,
  isAnnotationOnlyItem,
  lumenLabel,
  nameCandidatesFor,
  severityLabel,
} from "./formatItemValue";
import { CircleAlertGlyph, LockGlyph } from "./icons";
import {
  OTHER_LABEL_MAX_LENGTH,
  OTHER_REASON_CODE,
  reasonCodesForCategory,
  reasonLabel,
} from "./reasonCodes";
import {
  Btnk,
  ConflictBar,
  ConflictButtons,
  ConflictCopy,
  ConflictFootnote,
  ConflictHeading,
  ConflictStrip,
  DoneRow,
  SaveRetryLink,
  SaveStatus,
  TextArea,
  TextInput,
  TheirVersion,
} from "./reportStyled";
import { EditorReveal, FieldLabel, LabeledLine } from "./rowStyled";

export interface ChecklistRowEditorProps {
  item: Vero1ChecklistItem;
  /** Stages a sparse edit; commit dispatches now (discrete acts). */
  onFieldEdit: (edit: Vero1ItemEdit, opts?: { commit?: boolean }) => void;
  /** Drops pending fields — a flush must not send stale values. */
  onDropPending: (fields: Array<keyof Vero1ItemEdit>) => void;
  /** Flushes and collapses; never blocks (spec §09). */
  onDone: () => void;
  /** §9.4 Try-again — re-flushes the kept buffer. */
  onRetryFlush: () => void;
  /** Reload row: drop the buffer and collapse onto the latest version. */
  onReloadRow: () => void;
  saveState: AutosaveEntitySnapshot;
  reasonCatalog?: Vero1ReasonCatalog;
  /** Spec 4.4: the row moved under this edit — everything typed is kept, the
   *  banner names it, and Save applies the typed values over the latest. */
  conflict?: boolean;
  /** The refetch after a conflict hasn't landed yet — a retry now would
   *  resend the stale token, so Save waits for the fresh version. */
  conflictRefreshing?: boolean;
  /** The panel's centreRow — focusing a field below the fold centres it,
   *  the way the opening editor itself is centred. */
  onCentre?: (element: HTMLElement | null) => void;
}

/** In-row editor (design spec §04): opens beneath the row so the reasoning being changed stays on screen. */
export const ChecklistRowEditor = ({
  item,
  reasonCatalog,
  onFieldEdit,
  onDropPending,
  onDone,
  onRetryFlush,
  onReloadRow,
  saveState,
  conflict,
  conflictRefreshing,
  onCentre,
}: ChecklistRowEditorProps) => {
  // the open panel polls every 15s and replaces the cached checklist wholesale.
  // Field state is seeded once at mount, so reading the LIVE row for the save
  // diff, the save gate, or the editor's shape lets a poll move the ground
  // under the agent — silently dropping an override, demanding a reason for a
  // change they never made, or swapping the controls. Everything structural
  // reads what the row said at open; display and the conflict banner stay live.
  const centreField = (event: React.FocusEvent<HTMLElement>) => {
    const target = event.currentTarget;
    if (!onCentre || typeof requestAnimationFrame !== "function") return;
    requestAnimationFrame(() => onCentre(target));
  };
  const [showingTheirVersion, setShowingTheirVersion] = useState(false);
  const conflictAuthor = item.lastEditedByName || "Another analyst";
  // already possessive: the render sites read "<x> version", so a name that
  // needs an apostrophe carries it here rather than at every use
  const conflictAuthorPossessive = item.lastEditedByName
    ? `${item.lastEditedByName.split(" ")[0]}’s`
    : "their";
  const conflictWhen = item.lastEditedAt
    ? new Date(item.lastEditedAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      })
    : "an unknown time";
  const baseline = useRef({
    originalSeverity: item.originalSeverity,
    rowKind: item.rowKind,
    severities: availableSeverities(item),
    // the name the row stood at when the editor opened: a same-severity pick
    // of a different name is a moved result too (the server's result_moved)
    name:
      item.overrideOutcomeName ||
      clientNameFor(item.originalSeverity, item) ||
      "",
  }).current;
  const annotationOnly =
    baseline.rowKind === Vero1RowKind.Sentilink || isAnnotationOnlyItem(item);
  const isFact = baseline.rowKind === Vero1RowKind.Fact;
  const isCustom = baseline.rowKind === Vero1RowKind.Custom;
  const severities = annotationOnly || isFact ? [] : baseline.severities;
  const hasControl = severities.length > 0;
  const factValue = rowFactValue(item);

  // opens on Lumen's severity, so one editor covers overriding and annotating
  const [severity, setSeverity] = useState<Vero1Severity | null>(
    item.overrideSeverity ?? item.originalSeverity
  );
  // at Lumen's own severity the fired label is what the row already reads, so
  // the control opens on it; a stored pick always wins
  const [outcomeName, setOutcomeName] = useState(
    item.overrideOutcomeName ||
      (item.overrideSeverity === null
        ? clientNameFor(item.originalSeverity, item) ?? ""
        : "")
  );
  const [reasonCode, setReasonCode] = useState(item.reasonCode || "");
  const [otherLabel, setOtherLabel] = useState(item.otherReasonLabel || "");
  const [findings, setFindings] = useState(item.overrideExplanation || "");
  const [action, setAction] = useState(item.actionForLeasing || "");
  const [internalNote, setInternalNote] = useState(item.internalNote || "");
  // the note stays behind an affordance until it exists (spec 4.2) — an
  // always-open internal field invites narrating into the wrong box
  const [noteOpen, setNoteOpen] = useState(Boolean(item.internalNote));
  // the action follows the same pattern (design prototype): optional fields
  // stay collapsed until the agent reaches for them
  const [actionOpen, setActionOpen] = useState(Boolean(item.actionForLeasing));

  const lumenUnassessed = baseline.originalSeverity === null;

  const codes = reasonCodesForCategory(
    item.category,
    { hasWarnOption: severities.includes("warn") },
    reasonCatalog
  );
  const needsOtherLabel = reasonCode === OTHER_REASON_CODE;

  // the names configured at the staged severity; more than one is a pick the
  // agent owes before submit
  const nameOptions = hasControl ? nameCandidatesFor(severity, item) : [];
  const hasNameControl = nameOptions.length > 1;
  // a name the row no longer offers cannot stand as the current pick
  const chosenName = nameOptions.includes(outcomeName) ? outcomeName : "";
  /**
   * What each toggle reads. One configured name IS the result, so the toggle
   * wears it with the severity as subtext. Several names cannot be settled by
   * picking the severity alone, so the toggle keeps the severity word and says
   * a name is still owed — the tray below collects it.
   */
  const toggleNames = severities.reduce<
    Partial<Record<Vero1Severity, { name?: string; prompt?: boolean }>>
  >((acc, option) => {
    const names = nameCandidatesFor(option, item);
    if (names.length === 1) acc[option] = { name: displayName(names[0]) };
    else if (names.length > 1) acc[option] = { prompt: true };
    return acc;
  }, {});
  // once the agent has picked, the toggle wears the chosen name exactly as a
  // sole-name toggle does — the severity is the same qualifier either way, so
  // a settled multi-name row reads no differently from a settled single-name one
  if (severity !== null && chosenName && toggleNames[severity]) {
    toggleNames[severity] = { name: displayName(chosenName) };
  }
  // a long name reads poorly in a narrow share of the row — go full width
  const stacked = Object.values(toggleNames).some(
    (entry) => (entry?.name?.length ?? 0) > LONG_NAME_CHARS
  );
  // the toggle already shows the name, so the coloured line would repeat it
  const hasSoleName = severity !== null && Boolean(toggleNames[severity]?.name);

  // the result moving is what demands a reason (settled validation model);
  // findings are required by report time, not per save. A same-severity pick
  // of a different name moves the result too — the server's result_moved
  const severityMoves =
    hasControl && severity !== null && severity !== baseline.originalSeverity;
  const nameMoves =
    hasControl &&
    !severityMoves &&
    hasNameControl &&
    chosenName !== "" &&
    chosenName !== baseline.name;
  const resultMoves = severityMoves || nameMoves;

  // the server's override-removal cascade, mirrored so a later flush cannot
  // resurrect values it dropped
  const DEPENDENT_FIELDS: Array<keyof Vero1ItemEdit> = [
    "overrideOutcomeName",
    "reasonCode",
    "otherReasonLabel",
    "overrideExplanation",
    "actionForLeasing",
  ];
  const mirrorCascade = () => {
    setOutcomeName("");
    setReasonCode("");
    setOtherLabel("");
    setFindings("");
    setAction("");
    onDropPending(DEPENDENT_FIELDS);
  };

  // whether an override exists server-side now — reverting a row that never
  // held one must not clear fields the server never dropped
  const overrideLive = useRef(item.overrideSeverity !== null);

  /** Deselects the staged severity and the name that belonged to it. On a row
   *  Lumen never assessed this returns it to "no verdict"; on one Lumen did
   *  assess, clearing the override returns it to Lumen's own result — the same
   *  cascade a click on the baseline severity performs. */
  const clearSeverity = () => {
    const restored = isCustom ? null : baseline.originalSeverity;
    setSeverity(restored);
    if (overrideLive.current) {
      mirrorCascade();
      overrideLive.current = false;
      // the row reads Lumen's result again, so its fired name comes back with
      // it — mirrorCascade cleared the agent's pick, not the run's own word
      setOutcomeName(restored === null ? "" : clientNameFor(restored, item) ?? "");
      onFieldEdit(
        { overrideSeverity: null, overrideOutcomeName: "" },
        { commit: true }
      );
      return;
    }
    setOutcomeName(restored === null ? "" : clientNameFor(restored, item) ?? "");
    onDropPending(["overrideSeverity", "overrideOutcomeName"]);
  };

  const stageSeverity = (next: Vero1Severity) => {
    // clicking the staged severity again clears it: a row the agent has not
    // settled reads as unpicked rather than wearing a verdict it does not
    // carry, and the name picked at that severity goes with it
    if (severity === next) {
      clearSeverity();
      return;
    }
    setSeverity(next);
    // the name belongs to the severity it was picked at — a move empties it
    // so the agent re-picks from the new severity's own names
    setOutcomeName("");
    const removing = !isCustom && next === baseline.originalSeverity;
    if (removing) {
      if (overrideLive.current) {
        mirrorCascade();
        overrideLive.current = false;
      } else {
        onDropPending(["overrideSeverity", "overrideOutcomeName"]);
        return;
      }
      onFieldEdit(
        { overrideSeverity: null, overrideOutcomeName: "" },
        { commit: true }
      );
    } else {
      overrideLive.current = true;
      onFieldEdit(
        { overrideSeverity: next, overrideOutcomeName: "" },
        { commit: true }
      );
    }
  };

  const stageOutcomeName = (next: string) => {
    setOutcomeName(next);
    // the name only means anything alongside the severity it was picked at,
    // so an unmoved row stamps its severity with it
    onFieldEdit(
      severity !== null && severity !== baseline.originalSeverity
        ? { overrideOutcomeName: next }
        : { overrideSeverity: severity, overrideOutcomeName: next },
      { commit: true }
    );
  };

  /** The name picker. Rendered under the picked toggle when the group is
   *  stacked, else under the whole group — always attached to the result it
   *  names, never to whichever toggle happens to be last. */
  const nameTray =
    hasNameControl && severity !== null ? (
      <NameTray $tone={severity} data-testid="vero1-name-tray">
        <NameTrayLabel $tone={severity}>
          Leasing will read <RequiredMark>*</RequiredMark>
        </NameTrayLabel>
        <SelectField>
          {/* @ts-ignore — Select is untyped JS */}
          <Select
            dropdownClassName={VERO1_DROPDOWN_CLASS}
            id="vero1-outcome-name"
            value={chosenName || undefined}
            onChange={(next) => stageOutcomeName(String(next ?? ""))}
            placeholder="Select a name"
            data-testid="vero1-outcome-name-select"
          >
            {nameOptions.map((name) => (
              // @ts-ignore
              <Select.Option key={name} value={name}>
                {displayName(name)}
              </Select.Option>
            ))}
          </Select>
        </SelectField>
        {!chosenName && (
          <EditorHint data-testid="vero1-needs-name-hint">
            This result has more than one configured name. Pick the one leasing
            should read — required before submitting.
          </EditorHint>
        )}
      </NameTray>
    ) : null;

  // reason + OTHER label travel as one atomic group
  const stageReason = (next: string) => {
    setReasonCode(next);
    onFieldEdit(
      {
        reasonCode: next,
        otherReasonLabel: next === OTHER_REASON_CODE ? otherLabel.trim() : "",
      },
      { commit: true }
    );
  };
  const stageOtherLabel = (next: string) => {
    setOtherLabel(next);
    onFieldEdit({ reasonCode, otherReasonLabel: next.trim() });
  };

  const theirSeverity = effectiveTone(item);
  const theirWord = severityLabel(theirSeverity);
  const theirName = clientNameFor(theirSeverity, item);
  // the client's name is the result; the severity word is the fallback (AC 4)
  const theirResult = annotationOnly
    ? checkStateLabel(item.originalValue) ??
      clientNameFor(item.originalSeverity, item) ??
      severityLabel(item.originalSeverity)
    : theirName ?? theirWord;

  return (
    <EditorContainer $conflict={conflict} data-testid="vero1-row-editor">
      {/* the option menus render in a portal, outside this subtree */}
      <SelectFieldMenuStyles />
      {conflict && (
        <ConflictStrip $bleed data-testid="vero1-edit-conflict">
          <CircleAlertGlyph />
          <div>
            <ConflictHeading>
              Couldn&apos;t save — this row changed while you were editing
            </ConflictHeading>
            <ConflictCopy>
              <b>{conflictAuthor}</b> saved a result for {item.label} at{" "}
              {conflictWhen}. {AUTOSAVE_STRINGS.rowConflictBody}
              {conflictRefreshing &&
                " Fetching the latest version — Try again in a moment."}
            </ConflictCopy>
          </div>
        </ConflictStrip>
      )}
      {/* the row header above the card keeps the label and Lumen's reasoning
          on screen (spec §09) — the card opens on what Lumen said */}
      <LumenReference data-testid="vero1-lumen-reference">
        {annotationOnly ? (
          <>
            <span>Check result:</span>
            <LumenValue $tone={item.originalSeverity ?? undefined}>
              {lumenLabel(item)}
            </LumenValue>
          </>
        ) : (
          <>
            <span>Lumen:</span>
            {factValue !== null && (
              <LumenValue data-testid="vero1-lumen-value">
                {formatItemValue(item.originalValue, item.criterionKey)}
              </LumenValue>
            )}
            {factValue !== null && <span>·</span>}
            {lumenUnassessed ? (
              <span>{NO_VERDICT_LABEL}</span>
            ) : (
              <LumenValue $tone={item.originalSeverity ?? undefined}>
                {lumenLabel(item)}
              </LumenValue>
            )}
          </>
        )}
      </LumenReference>

      {annotationOnly && (
        <>
          <EditorHint data-testid="vero1-annotation-only-hint">
            The result is the check&apos;s own and can&apos;t be overridden. To
            clear it, verify the check on the applicant&apos;s identity
            verification — screening re-runs and this row updates.
          </EditorHint>
          <Spacer size={Spacer.SIZES.sm} />
        </>
      )}

      {isFact && (
        <>
          <EditorHint data-testid="vero1-fact-hint">
            A measured value with no configured outcome — read-only. Your
            findings, action and note are recorded against it.
          </EditorHint>
          <Spacer size={Spacer.SIZES.sm} />
        </>
      )}

      {hasControl && (
        <EditorField>
          <EditorLabel>
            Result <RequiredMark>*</RequiredMark>
          </EditorLabel>
          <SeverityToggleGroup
            $attached={hasNameControl && !stacked}
            $stacked={stacked}
          >
            {severities.map((option) => {
              const entry = toggleNames[option];
              // stacked toggles are full-width rows, so the tray can sit
              // directly under the picked one the way the recommendation does
              const trayHere = stacked && hasNameControl && severity === option;
              return (
                <React.Fragment key={option}>
                  <SeverityToggle
                    type="button"
                    $tone={option}
                    $stacked={stacked}
                    $active={severity === option}
                    aria-pressed={severity === option}
                    // the client's name is the result; the severity qualifies it
                    aria-label={
                      entry?.name
                        ? `${entry.name} — ${SEVERITY_WORDS[option]}`
                        : entry?.prompt
                        ? `${SEVERITY_WORDS[option]} — select a name`
                        : SEVERITY_WORDS[option]
                    }
                    title={
                      severity === option ? "Click again to clear" : undefined
                    }
                    onClick={() => stageSeverity(option)}
                  >
                    {entry?.name ? (
                      <SeverityStack $inline={stacked}>
                        <SeverityStackName>{entry.name}</SeverityStackName>
                        <SeverityStackWord>
                          {SEVERITY_WORDS[option]}
                        </SeverityStackWord>
                      </SeverityStack>
                    ) : (
                      SEVERITY_WORDS[option]
                    )}
                  </SeverityToggle>
                  {trayHere && nameTray}
                </React.Fragment>
              );
            })}
          </SeverityToggleGroup>
          {!stacked && nameTray}
          {lumenUnassessed && (
            <EditorHint>
              {isCustom
                ? // custom rows are checks Lumen never performed — the API
                  // blocks Approve/Conditional/Decline submits until assessed
                  "Not assessed by Lumen — your result stands alone. Required before submitting (except on Cancel)."
                : "Lumen didn't assess this. Optional — you can submit the review without it."}
            </EditorHint>
          )}
          {!lumenUnassessed && severity === baseline.originalSeverity && (
            <EditorHint>
              Unchanged from Lumen. Your findings will be recorded against the
              row either way.
            </EditorHint>
          )}
        </EditorField>
      )}

      {hasControl && (
        <EditorField>
          <EditorLabel>
            {resultMoves
              ? "Reason * — required when the result changes"
              : "Reason — optional"}
          </EditorLabel>
          {/* @ts-ignore — Select is untyped JS */}
          <SelectField>
            <Select
              dropdownClassName={VERO1_DROPDOWN_CLASS}
              id="vero1-reason"
              value={reasonCode || undefined}
              onChange={(next) => stageReason(String(next ?? ""))}
              placeholder="Select a reason"
            >
              {codes.map((code) => (
                // @ts-ignore
                <Select.Option key={code} value={code}>
                  {reasonLabel(code, reasonCatalog)}
                </Select.Option>
              ))}
            </Select>
          </SelectField>
          {!resultMoves && (
            <EditorHint>
              Optional while the result stays as screening had it.
            </EditorHint>
          )}
        </EditorField>
      )}

      {needsOtherLabel && (
        <EditorField>
          <EditorLabel>
            Short label <RequiredMark>*</RequiredMark>
          </EditorLabel>
          <TextInput
            id="vero1-other-label"
            onFocus={centreField}
            name="vero1-other-label"
            value={otherLabel}
            maxLength={OTHER_LABEL_MAX_LENGTH}
            onChange={(e) => stageOtherLabel(e.target.value)}
          />
          <EditorHint>
            A short name for the reason, max {OTHER_LABEL_MAX_LENGTH} chars.
            Explain it in Findings below.
          </EditorHint>
        </EditorField>
      )}

      <EditorField>
        <EditorLabel>
          Findings for leasing * — required before the report
        </EditorLabel>
        <TextArea
          id="vero1-findings"
          onFocus={centreField}
          name="vero1-findings"
          value={findings}
          rows={4}
          onChange={(e) => {
            setFindings(e.target.value);
            onFieldEdit({ overrideExplanation: e.target.value.trim() });
          }}
        />
        <EditorHint>Leasing reads this verbatim.</EditorHint>
      </EditorField>

      {actionOpen ? (
        <EditorField>
          <EditorLabel>Action for leasing — optional</EditorLabel>
          <TextArea
            id="vero1-action"
            onFocus={centreField}
            name="vero1-action"
            value={action}
            rows={2}
            onChange={(e) => {
              setAction(e.target.value);
              onFieldEdit({ actionForLeasing: e.target.value.trim() });
            }}
          />
          <EditorHint>
            Carried onto the leasing report exactly as written, alongside the
            other items&apos; actions.
          </EditorHint>
        </EditorField>
      ) : (
        <EditorField>
          <EditorReveal
            type="button"
            data-testid="vero1-add-action"
            onClick={() => setActionOpen(true)}
          >
            + Add action for leasing
          </EditorReveal>
        </EditorField>
      )}

      {noteOpen ? (
        <EditorField>
          <EditorLabel>
            Internal-only note{" "}
            <LockGlyph size={11} strokeWidth={1.9} cornerRadius={1.5} /> —
            optional
          </EditorLabel>
          <TextArea
            id="vero1-internal-note"
            onFocus={centreField}
            name="vero1-internal-note"
            value={internalNote}
            rows={2}
            onChange={(e) => {
              setInternalNote(e.target.value);
              onFieldEdit({ internalNote: e.target.value.trim() });
            }}
          />
          <EditorHint>Never shown to the client.</EditorHint>
        </EditorField>
      ) : (
        <EditorField>
          <EditorReveal
            type="button"
            data-testid="vero1-add-internal-note"
            onClick={() => setNoteOpen(true)}
          >
            + Add internal note
          </EditorReveal>
        </EditorField>
      )}

      {conflict && showingTheirVersion && !conflictRefreshing && (
        <TheirVersion data-testid="vero1-their-version">
          <LabeledLine>
            <FieldLabel>
              {annotationOnly ? "Check result" : "Result"}
            </FieldLabel>
            <SmallText>{theirResult}</SmallText>
          </LabeledLine>
          {item.overrideExplanation && (
            <LabeledLine>
              <FieldLabel>Findings for leasing</FieldLabel>
              <SmallText>{item.overrideExplanation}</SmallText>
            </LabeledLine>
          )}
        </TheirVersion>
      )}
      {conflict ? (
        <ConflictBar>
          <ConflictButtons>
            <Btnk
              type="button"
              $variant="accent"
              $height={34}
              disabled={conflictRefreshing}
              onClick={onRetryFlush}
            >
              {AUTOSAVE_STRINGS.tryAgain}
            </Btnk>
            <Btnk
              type="button"
              $variant="ghost"
              $height={34}
              data-testid="vero1-see-their-version"
              disabled={conflictRefreshing}
              onClick={() => setShowingTheirVersion((open) => !open)}
            >
              {showingTheirVersion ? "Hide" : "See"} {conflictAuthorPossessive}{" "}
              version
            </Btnk>
            <Btnk
              type="button"
              $variant="link"
              $height={34}
              data-testid="vero1-reload-row"
              onClick={onReloadRow}
            >
              Reload row
            </Btnk>
          </ConflictButtons>
          <ConflictFootnote>
            Reloading replaces this row with {conflictAuthorPossessive} saved
            version and discards what you typed. Copy your findings first.
          </ConflictFootnote>
        </ConflictBar>
      ) : (
        <DoneRow>
          <Btnk
            type="button"
            $variant="accent"
            $height={34}
            data-testid="vero1-done"
            onClick={onDone}
          >
            {AUTOSAVE_STRINGS.done}
          </Btnk>
          {saveState.status === "failed" ? (
            <SaveStatus data-testid="vero1-save-status" $failed>
              <CircleAlertGlyph size={12} />
              {AUTOSAVE_STRINGS.failedHeading}
              <SaveRetryLink
                type="button"
                data-testid="vero1-save-retry"
                onClick={onRetryFlush}
              >
                {AUTOSAVE_STRINGS.tryAgain}
              </SaveRetryLink>
            </SaveStatus>
          ) : (
            <SaveStatus data-testid="vero1-save-status">
              {saveState.status === "saving"
                ? AUTOSAVE_STRINGS.saving
                : saveState.savedAt !== null
                ? AUTOSAVE_STRINGS.savedAt(autosaveTimeLabel(saveState.savedAt))
                : ""}
            </SaveStatus>
          )}
        </DoneRow>
      )}
    </EditorContainer>
  );
};
